import React, { useMemo, useState, useEffect } from "react";
import { Minus, Plus, Flame } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useCart } from "@/context/CartContext";
import { money } from "@/lib/api";

const SPICE = ["Mild", "Jamaican Medium", "Authentic Jerk Fire 🔥"];
const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function ItemDialog({ item, open, onClose }) {
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  const [spice, setSpice] = useState("Jamaican Medium");
  const [addons, setAddons] = useState([]);
  const [notes, setNotes] = useState("");
  const [option, setOption] = useState(null);
  const [flavor, setFlavor] = useState(null);
  const [rice, setRice] = useState(null);
  const [sides, setSides] = useState([]);

  const hasOptions = item?.options?.length > 0;
  const hasFlavors = item?.flavors?.length > 0;
  const hasRice = item?.rice_options?.length > 0;
  const sidesCount = Number(item?.sides_count || 0);
  const hasSides = item?.side_options?.length > 0 && sidesCount > 0;

  useEffect(() => {
    if (open) {
      setQty(1); setSpice("Jamaican Medium"); setAddons([]); setNotes("");
      setOption(item?.options?.length ? item.options[0].name : null);
      setFlavor(item?.flavors?.length ? item.flavors[0] : null);
      setRice(item?.rice_options?.length ? item.rice_options[0] : null);
      setSides([]);
    }
  }, [open, item]);

  const addonPrice = useMemo(
    () => (item?.addons || []).filter((a) => addons.includes(a.name)).reduce((s, a) => s + Number(a.price || 0), 0),
    [addons, item]
  );
  if (!item) return null;
  const optObj = hasOptions ? (item.options.find((o) => o.name === option) || item.options[0]) : null;
  const basePrice = hasOptions ? Number(optObj.price) : Number(item.price);
  const unitPrice = basePrice + addonPrice;

  const toggleAddon = (name) =>
    setAddons((p) => (p.includes(name) ? p.filter((x) => x !== name) : [...p, name]));

  const toggleSide = (name) =>
    setSides((p) => {
      if (p.includes(name)) return p.filter((x) => x !== name);
      if (p.length >= sidesCount) return p;
      return [...p, name];
    });

  const sidesOk = !hasSides || sides.length === sidesCount;
  const canAdd = sidesOk && !item.sold_out;

  const submit = () => {
    if (!canAdd) return;
    addItem({
      item_id: item.item_id,
      name: item.name,
      image: item.image,
      category_id: item.category_id,
      basePrice,
      selected_option: hasOptions ? option : null,
      selected_flavor: hasFlavors ? flavor : null,
      selected_rice: hasRice ? rice : null,
      selected_sides: hasSides ? sides : [],
      quantity: qty,
      spice_level: item.spice ? spice : null,
      addons,
      special_instructions: notes,
      unitPrice,
      lineTotal: unitPrice * qty,
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#181D1A] border-emerald-800/60 text-[#FDFBF7] max-w-lg p-0 overflow-hidden gap-0 max-h-[92vh] overflow-y-auto no-scrollbar">
        <DialogTitle className="sr-only">{item.name}</DialogTitle>
        <DialogDescription className="sr-only">Customize {item.name} and add it to your cart</DialogDescription>
        <div className="relative aspect-[16/9]">
          <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#181D1A] to-transparent" />
        </div>
        <div className="p-6">
          <div className="flex items-start justify-between gap-3">
            <h2 className="font-display font-black text-2xl">{item.name}</h2>
            <span className="font-display font-black text-amber-400 text-2xl">{money(basePrice)}</span>
          </div>
          <p className="text-sm text-[#FDFBF7]/65 mt-2">{item.description}</p>

          {hasOptions && (
            <div className="mt-5">
              <div className="font-display font-bold uppercase tracking-wide text-sm text-amber-400 mb-2">Choose Size / Option</div>
              <div className="flex flex-wrap gap-2">
                {item.options.map((o) => (
                  <button
                    key={o.name}
                    data-testid={`modifier-option-${slug(o.name)}`}
                    onClick={() => setOption(o.name)}
                    className={`px-3 py-2 rounded-full text-sm font-semibold border transition-colors ${
                      option === o.name ? "bg-amber-400 text-[#0A2818] border-amber-400" : "border-emerald-700/50 text-[#FDFBF7]/80 hover:border-amber-400"
                    }`}
                  >
                    {o.name} · {money(o.price)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {hasFlavors && (
            <div className="mt-5">
              <div className="font-display font-bold uppercase tracking-wide text-sm text-amber-400 mb-2">Choose Flavor</div>
              <div className="flex flex-wrap gap-2">
                {item.flavors.map((fl) => (
                  <button
                    key={fl}
                    data-testid={`modifier-flavor-${slug(fl)}`}
                    onClick={() => setFlavor(fl)}
                    className={`px-3 py-2 rounded-full text-sm font-semibold border transition-colors ${
                      flavor === fl ? "bg-amber-400 text-[#0A2818] border-amber-400" : "border-emerald-700/50 text-[#FDFBF7]/80 hover:border-amber-400"
                    }`}
                  >
                    {fl}
                  </button>
                ))}
              </div>
            </div>
          )}

          {item.spice && (
            <div className="mt-5">
              <div className="font-display font-bold uppercase tracking-wide text-sm text-amber-400 mb-2 flex items-center gap-2">
                <Flame className="w-4 h-4" /> Spice Level
              </div>
              <div className="flex flex-wrap gap-2">
                {SPICE.map((s, i) => (
                  <button
                    key={s}
                    data-testid={`modifier-modal-spice-level-${["mild","medium","authentic"][i]}`}
                    onClick={() => setSpice(s)}
                    className={`px-3 py-2 rounded-full text-sm font-semibold border transition-colors ${
                      spice === s ? "bg-amber-400 text-[#0A2818] border-amber-400" : "border-emerald-700/50 text-[#FDFBF7]/80 hover:border-amber-400"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {item.addons?.length > 0 && (
            <div className="mt-5">
              <div className="font-display font-bold uppercase tracking-wide text-sm text-amber-400 mb-2">Add-ons</div>
              <div className="space-y-2">
                {item.addons.map((a) => (
                  <label
                    key={a.name}
                    data-testid={`modifier-addon-${slug(a.name)}`}
                    className="flex items-center justify-between px-4 py-2.5 rounded-xl border border-emerald-800/50 cursor-pointer hover:border-amber-400/50"
                  >
                    <span className="flex items-center gap-3">
                      <input type="checkbox" checked={addons.includes(a.name)} onChange={() => toggleAddon(a.name)} className="accent-amber-400 w-4 h-4" />
                      {a.name}
                    </span>
                    {Number(a.price) > 0 && <span className="text-amber-400 font-semibold">+{money(a.price)}</span>}
                  </label>
                ))}
              </div>
            </div>
          )}

          {hasRice && (
            <div className="mt-5">
              <div className="font-display font-bold uppercase tracking-wide text-sm text-amber-400 mb-2">Choose Your Rice</div>
              <div className="flex flex-wrap gap-2">
                {item.rice_options.map((r) => (
                  <button
                    key={r}
                    data-testid={`modifier-rice-${slug(r)}`}
                    onClick={() => setRice(r)}
                    className={`px-3 py-2 rounded-full text-sm font-semibold border transition-colors ${
                      rice === r ? "bg-amber-400 text-[#0A2818] border-amber-400" : "border-emerald-700/50 text-[#FDFBF7]/80 hover:border-amber-400"
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
          )}

          {hasSides && (
            <div className="mt-5">
              <div className="font-display font-bold uppercase tracking-wide text-sm text-amber-400 mb-2">
                Choose {sidesCount} Side{sidesCount > 1 ? "s" : ""} <span className="text-[#FDFBF7]/50 normal-case font-normal">({sides.length}/{sidesCount})</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {item.side_options.map((sd) => {
                  const active = sides.includes(sd);
                  const disabled = !active && sides.length >= sidesCount;
                  return (
                    <button
                      key={sd}
                      data-testid={`modifier-side-${slug(sd)}`}
                      onClick={() => toggleSide(sd)}
                      disabled={disabled}
                      className={`px-3 py-2 rounded-full text-sm font-semibold border transition-colors ${
                        active ? "bg-amber-400 text-[#0A2818] border-amber-400" : "border-emerald-700/50 text-[#FDFBF7]/80 hover:border-amber-400 disabled:opacity-30 disabled:hover:border-emerald-700/50"
                      }`}
                    >
                      {sd}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-5">
            <div className="font-display font-bold uppercase tracking-wide text-sm text-amber-400 mb-2">Special Instructions</div>
            <textarea
              data-testid="modifier-special-instructions"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Extra crispy, no scallions, allergies…"
              className="w-full rounded-xl bg-[#101311] border border-emerald-800/50 px-4 py-3 text-sm outline-none focus:border-amber-400"
            />
          </div>

          <div className="mt-4 flex items-center gap-4 sticky bottom-0 z-10 bg-[#181D1A] py-3 border-t border-emerald-800/40">
            <div className="flex items-center gap-3 bg-[#101311] rounded-full border border-emerald-800/50 px-2 py-1.5">
              <button data-testid="modifier-qty-decrease" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-8 h-8 grid place-items-center rounded-full hover:bg-emerald-900"><Minus className="w-4 h-4" /></button>
              <span data-testid="modifier-qty-value" className="font-display font-black text-lg w-6 text-center">{qty}</span>
              <button data-testid="modifier-qty-increase" onClick={() => setQty((q) => q + 1)} className="w-8 h-8 grid place-items-center rounded-full hover:bg-emerald-900"><Plus className="w-4 h-4" /></button>
            </div>
            <button
              data-testid="modifier-modal-add-to-cart-button"
              onClick={submit}
              disabled={!canAdd}
              className="flex-1 py-3.5 rounded-full bg-amber-400 hover:bg-amber-300 disabled:opacity-40 disabled:cursor-not-allowed text-[#0A2818] font-display font-black uppercase tracking-wide active:scale-95 transition-transform"
            >
              {item.sold_out ? "Sold Out" : hasSides && !sidesOk ? `Pick ${sidesCount} side${sidesCount > 1 ? "s" : ""}` : `Add · ${money(unitPrice * qty)}`}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
